<template>
  <v-main>
    <v-app-bar app flat light color="white">
      <AppbarBackIcon />
      <v-toolbar-title class="font-weight-bold">
        <span>订单</span>
        <template v-if="business_order_id">#{{ business_order_id }}</template>
      </v-toolbar-title>
      <v-spacer></v-spacer>
    </v-app-bar>
    <v-container fluid>
      <v-simple-table v-if="payment">
        <template v-slot:default>
          <thead></thead>
          <tbody>
            <tr v-for="(value, key) in payment" :key="key">
              <td>{{ key }}</td>
              <td>
                <router-link v-if="isLinkField(key)" class="payment-field-value" :to="paymentFieldLink(key, value)">{{
                  paymentFieldLinkText(key, value)
                }}</router-link>
                <pre v-else class="payment-field-value">{{ formatPaymentFieldValue(key, value) }}</pre>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <v-text-field label="备注" class="mt-8" v-model="form.note"></v-text-field>
      <div class="d-flex justify-space-around">
        <v-btn color="primary" v-if="canFinish" @click="onFinish">结束</v-btn>
        <v-btn color="primary" v-if="canConfirm" @click="onConfirm">确认</v-btn>
        <v-btn color="secondary" v-if="canRevert" @click="onRevert">撤销</v-btn>
        <v-tooltip v-if="canRefund" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn color="warning" @click="onRefund" v-bind="attrs" v-on="on">退款</v-btn>
          </template>
          <span>目前仅标记退款，需自行在支付渠道退款给用户</span>
        </v-tooltip>
      </div>
      <div class="text-h6 mt-16">订单日志</div>
      <pre class="payment-logs">{{ payment_logs }}</pre>
    </v-container>
  </v-main>
</template>

<script>
import _ from 'lodash'
import { PaymentChannelType, PaymentDeviceType, PaymentStatus } from '@/plugins/payment'
import AppbarBackIcon from '@/components/AppbarBackIcon.vue'
import { paymentStore } from '@/store/payment'
import { homeStore } from '@/store/home'
import { paymentChannelStore } from '@/store/paymentChannel'
import { formatCurrency, formatPrice } from '@/plugins/price'
import { projectStore } from '@/store/project'
import { productStore } from '@/store/product'
import { paywallStore } from '@/store/paywall'
import { customerStore } from '@/store/customer'

const LINK_FIELD_CONFIG = {
  project_id: {
    path: '/dash/project-detail',
    getName: (value) => {
      return projectStore.get(value)?.name
    }
  },
  paywall_id: {
    path: '/dash/paywall-detail',
    getName: (value) => {
      return paywallStore.get(value)?.name
    }
  },
  product_id: {
    path: '/dash/product-detail',
    getName: (value) => {
      return productStore.get(value)?.name
    }
  },
  channel_id: {
    path: '/dash/payment-channel-detail',
    getName: (value) => {
      return paymentChannelStore.get(value)?.name
    }
  },
  customer_id: {
    path: '/dash/customer-detail',
    getName: (value) => {
      return customerStore.get(value)?.nickname
    }
  },
}

export default {
  name: 'PaymentOrderDetailPage',
  components: { AppbarBackIcon },
  data() {
    return {
      payment_logs: null,
      form: {
        note: null,
      },
    }
  },
  computed: {
    business_order_id() {
      return this.$route.query.business_order_id
    },
    payment() {
      if (!this.business_order_id) {
        return null
      }
      return paymentStore.get(this.business_order_id)
    },
    paymentChannel() {
      if (_.isNil(this.payment)) {
        return null
      }
      return paymentChannelStore.get(this.payment.payment_channel_id)
    },
    canConfirm() {
      return !_.isNil(this.payment) && this.payment.status === PaymentStatus.PAID
    },
    canRevert() {
      return !_.isNil(this.payment) && this.payment.status === PaymentStatus.PAID
    },
    canRefund() {
      return !_.isNil(this.payment) && this.payment.status === PaymentStatus.CONFIRMED
    },
    canFinish() {
      if (_.isNil(this.payment)) {
        return false
      }
      let channelType = this.paymentChannel?.type
      let isStandard = PaymentChannelType.isStandard(channelType)
      let status = this.payment.status
      if (status === PaymentStatus.INIT || status === PaymentStatus.UNPAID) {
        return true
      }
      if (isStandard && status === PaymentStatus.PAID) {
        return true
      }
      return false
    },
  },
  async mounted() {
    await homeStore.doLoad()
    if (_.isNil(this.payment)) {
      await paymentStore.doQuery({
        business_order_id: this.business_order_id,
      })
    }
    let log_s = await paymentStore.doQueryLog({
      business_order_id: this.business_order_id,
    })
    this.payment_logs = JSON.stringify(log_s, null, 2)
    await productStore.doLoad()
    await paywallStore.doLoad()
  },
  methods: {
    statusNameOf(status) {
      let name = PaymentStatus.nameOf(_.parseInt(status))
      return name ? name : status
    },
    formatDeviceType(value) {
      return PaymentDeviceType.nameOf(value) || value
    },
    formatChannelType(value) {
      return PaymentChannelType.nameOf(value) || value
    },
    formatPriceField(value) {
      let valueText = formatPrice(value, this.payment.currency)
      let currencyText = formatCurrency(this.payment.currency)
      return `${value} (${valueText}${currencyText})`
    },
    formatPaymentFieldValue(key, value) {
      if (key.includes('status')) {
        return this.statusNameOf(value)
      }
      if (key === 'device_type') {
        return this.formatDeviceType(value)
      }
      if (key === 'channel_type') {
        return this.formatChannelType(value)
      }
      if (key.endsWith('_price')) {
        return this.formatPriceField(value)
      }
      if (_.isPlainObject(value) || _.isArray(value)) {
        return JSON.stringify(value, null, 2)
      }
      return value
    },
    isLinkField(key) {
      return !!LINK_FIELD_CONFIG[key]
    },
    paymentFieldLink(key, value) {
      let config = LINK_FIELD_CONFIG[key]
      return {
        path: config.path,
        query: { id: value }
      }
    },
    paymentFieldLinkText(key, value) {
      let config = LINK_FIELD_CONFIG[key]
      let name = config.getName(value)
      if (!name) {
        return value
      }
      return `${value} (${name})`
    },
    async _handleOperate(operator) {
      await paymentStore[operator]({
        business_order_id: this.business_order_id,
        note: this.form.note,
      })
      this.$toasted.success('操作成功')
    },
    async onConfirm() {
      await this._handleOperate('doConfirm')
    },
    async onRevert() {
      await this._handleOperate('doRevert')
    },
    async onRefund() {
      await this._handleOperate('doRefund')
    },
    async onFinish() {
      await paymentStore.doFinish({
        business_order_id: this.business_order_id,
      })
      if (this.payment.status === PaymentStatus.CONFIRMED) {
        this.$toasted.success('订单支付成功')
      } else {
        this.$toasted.info('订单取消成功')
      }
    },
  },
}
</script>

<style lang="less" scoped>
.payment-logs {
  font-size: 12px;
  white-space: break-spaces;
  word-break: break-all;
}

.payment-field-value {
  max-height: 160px;
  overflow: auto;
}
</style>
